import Autosizer from "react-virtualized-auto-sizer";
import { FixedSizeList as VirtualList } from "react-window";

import { Collapse } from "antd";
import { setSelectedShapefile } from "store/features/project/projectSlice";
import styled from "styled-components";

import { useUser } from "hooks";

import { GeomT } from "models/model";

import { ColorPicker } from "components/base";
import { IconSpinner } from "components/icons";
import { createFeatureRowRenderer } from "components/project/shapefiles/components/shared/createFeatureRowRenderer";
import {
  EditableGeomProperties,
  useUpdateBatchProjectGeomsMutation,
  useUpdateProjectGeomMutation
} from "components/project/shapefiles/mutations";

interface IShapefileFeatureGroup {
  groups: GeomT[][];
  shapefileFeaturePropertyKey;
  shapefileFeatures;
  selectedShapefile;
}

const featureRowRenderer = createFeatureRowRenderer({
  showCheckbox: false,
  showPropertyTitle: false
});

export default function OrganizationShapefileFeatureGroup({
  groups,
  shapefileFeaturePropertyKey,
  selectedShapefile
}: IShapefileFeatureGroup) {
  const { isAtLeastPowerUser } = useUser();
  const batchGeomUpdater = useUpdateBatchProjectGeomsMutation({
    onSuccess: () => {
      setSelectedShapefile({ ...selectedShapefile });
    }
  });

  const updateProjectGeom = useUpdateProjectGeomMutation({
    onSuccess: () => {
      setSelectedShapefile({ ...selectedShapefile });
    }
  });

  function updateFeatureProps(featureId, oldFeature, prop, value) {
    updateProjectGeom.mutate({
      body: {
        color: oldFeature.Color,
        thickness: oldFeature.Thickness,
        strokeColor: oldFeature.StrokeColor,
        [prop]: value
      },
      shapeFileGeomId: featureId
    });
  }

  function updateShapefileGeoms(
    currentGeomGroup: GeomT[],
    newProperties: EditableGeomProperties,
    propertyKey,
    value
  ) {
    const geomIds = currentGeomGroup.map((geom) => geom.shapefileGeomId);
    batchGeomUpdater.mutateAsync({
      shapeFileGeomIds: geomIds,
      update: {
        ...newProperties,
        [propertyKey]: value
      }
    });
  }

  return (
    <Collapse
      collapsible={"icon"}
      expandIcon={({ isActive }) => (
        <div className={`orgShapefileCollapseChevron ${isActive ? "active" : ""}`}></div>
      )}>
      {groups?.map((featureGroup, i) => {
        const firstGeom = featureGroup?.[0];
        const geomGroupTitle = firstGeom?.Properties?.[shapefileFeaturePropertyKey];
        return (
          <StyledPanel
            header={
              <GroupedFeatureHeaderWrapper>
                <ColorPicker
                  isReadOnly={!isAtLeastPowerUser}
                  onChange={(propertyKey, value) =>
                    updateShapefileGeoms(featureGroup, null, propertyKey, value)
                  }
                  values={{
                    color: firstGeom?.Color || selectedShapefile?.layer?.style?.color,
                    strokeColor:
                      firstGeom?.StrokeColor ||
                      selectedShapefile?.layer?.style?.strokeColor,
                    thickness:
                      firstGeom?.Thickness || selectedShapefile?.layer?.style?.thickness,
                    opacity:
                      (firstGeom?.Opacity ||
                        selectedShapefile?.layer?.style?.opacity ||
                        1) * 100
                  }}
                />
                <StyledGeomGroupTitle>{geomGroupTitle}</StyledGeomGroupTitle>
              </GroupedFeatureHeaderWrapper>
            }
            // If animation doesn't feel right, we'll need to know when map/project tree is finished loading
            // as this purely accounts for the update
            extra={
              batchGeomUpdater.isLoading &&
              batchGeomUpdater.variables.shapeFileGeomIds?.[0] ===
                firstGeom.shapefileGeomId && <IconSpinner size={14} />
            }
            key={`${geomGroupTitle}${i}`}>
            <GroupedFeaturesContainer>
              <Autosizer>
                {({ height, width }) => {
                  return (
                    <VirtualList
                      className="List"
                      height={height}
                      width={width}
                      itemCount={featureGroup?.length ?? 0}
                      itemSize={35}
                      itemData={{
                        features: featureGroup,
                        shapefileFeaturePropertyKey,
                        updateFeatureProps,
                        isReadOnly: !isAtLeastPowerUser,
                        selectedShapefile
                      }}>
                      {featureRowRenderer}
                    </VirtualList>
                  );
                }}
              </Autosizer>
            </GroupedFeaturesContainer>
          </StyledPanel>
        );
      })}
    </Collapse>
  );
}

const GroupedFeaturesContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 30vh;

  .List {
    padding: 0 4px;
  }
`;

const GroupedFeatureHeaderWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 6px;
`;

const StyledGeomGroupTitle = styled.span`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const StyledPanel = styled(Collapse.Panel)`
  .ant-collapse-header {
    align-items: center !important;
  }

  .ant-collapse-expand-icon:hover {
    .orgShapefileCollapseChevron::after {
      border-color: var(--color-primary);
    }
  }

  .orgShapefileCollapseChevron {
    transition: all 0.2s;
    transform: rotate(270deg);
    vertical-align: 0 !important;

    &::after {
      content: "";
      display: block;
      width: 7px;
      height: 7px;
      border: 1px solid var(--color-text);
      border-width: 0 1px 1px 0;
      transform: rotate(45deg);
    }

    &.active {
      transform: rotate(360deg);
    }
  }
`;
